import React from 'react';
import { Link } from 'react-router-dom';
import './AboutPage.css';

function AboutPage() {
  const bannerStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/assets/NeXusNeuroClinical_neuc.webp)`,
  };

  return (
    <main className="about-page">
      <section className="about-banner" style={bannerStyle}>
        <div className="about-banner-overlay">
          <Link to="/" className="about-back">&larr; Home</Link>
          <img
            src="/assets/nexus_neuro_logo.webp"
            alt="NeXus NeuroInformatics, Inc."
            className="about-banner-logo"
          />
          <h1 className="about-banner-title">About Us</h1>
        </div>
      </section>

      <header className="about-header">
        <p className="about-deck">
          We assist researchers with <strong>Auditable AI</strong>.
        </p>
      </header>

      <section className="about-body">

        <article className="about-section">
          <h2 className="about-h2">The Origin</h2>
          <p>
            NeXus exists because a biochemist watched neurochemistry operate in
            real time, through medications, behavior, recovery, relapse, and the
            countless small decisions that shape human outcomes, and concluded
            that the dynamics of biology are knowable, modelable, and worth
            simulating before the cost of laboratory or clinical validation is
            incurred.
          </p>
          <p>
            That conviction became AETHER (Adaptive Endocrine Transformer Heads
            with Emotional Regulation): a transformer architecture in which the
            same pharmacokinetic principles that govern how drugs are absorbed,
            distributed, metabolized, and eliminated also govern how the model
            assigns significance to experience over time. AETHER was developed
            and patented by our parent corporation,{' '}
            <a href="https://nexusconcordat.com">Nexus Concordat, Inc.</a>
          </p>
        </article>

        <article className="about-section">
          <h2 className="about-h2">The Corporate Structure</h2>
          <p>
            We operate as one of three sister entities under a common parent:
          </p>
          <div className="about-entity-stack">
            <div className="about-entity">
              <span className="about-entity-role">Parent &middot; IP Holder</span>
              <h3>Nexus Concordat, Inc.</h3>
              <p>
                Owns the AETHER architecture, holds the patents, and licenses
                the technology to the operating subsidiaries under field-of-use
                agreements.
              </p>
            </div>
            <div className="about-entity">
              <span className="about-entity-role">FDA-Facing Operating Subsidiary</span>
              <h3>NeXus NeuroInformatics, Inc.</h3>
              <p>
                Commercializes AETHER for biomedical research, in silico drug
                development, synthetic data for pharmaceutical and clinical
                research, and regulatory decision-support tooling.
              </p>
            </div>
            <div className="about-entity">
              <span className="about-entity-role">Consumer Operating Subsidiary</span>
              <h3>Dynamic Hallucination, Inc.</h3>
              <p>
                Operates a governed interactive role-playing platform whose
                player interactions generate the structured behavioral training
                pairs that NeXus uses to train its biomedical AI.
              </p>
            </div>
          </div>
        </article>

        <article className="about-section">
          <h2 className="about-h2">Our Conviction</h2>
          <p>
            It is our conviction that biochemistry is deterministic by nature.
            Where direct human study is difficult, impractical, expensive, or
            unethical, NeXus proposes an <em>in silico</em> simulation
            environment to model those biological contexts and generate testable
            hypotheses for subsequent validation.
          </p>
          <p>
            Our systems are decision-support infrastructure for early-stage
            research. They are not clinical diagnostic tools and are not
            authorized medical devices except where and when explicit regulatory
            clearance has been granted for a specific intended use.
          </p>
        </article>

        <article className="about-section">
          <h2 className="about-h2">Our Public Record</h2>
          <p>
            Nexus Concordat filed public comment on FDA Docket{' '}
            <strong>FDA-2026-N-4390</strong> (AI-Enabled Optimization of
            Early-Phase Clinical Trials Pilot Program) on April 29, 2026,
            accompanied by a methods paper titled <em>&ldquo;Mechanistic
            In-Silico Trial Emulation: A Third Evidentiary Substrate for
            Real-World Evidence.&rdquo;</em> The comment is publicly available
            at regulations.gov.
          </p>
          <p>
            Four U.S. provisional patent applications cover the architecture:{' '}
            <strong>63/939,190</strong> (Substrate-Independent Memory Weighting),{' '}
            <strong>63/962,385</strong> (Neurochemical Language Model),{' '}
            <strong>63/988,485</strong>, and <strong>64/034,536</strong>.
          </p>
        </article>

        <article className="about-section">
          <h2 className="about-h2">What&rsquo;s Next</h2>
          <p>
            We are building partnerships with pre-clinical research groups,
            regulatory advisors, and institutional collaborators willing to
            evaluate <em>in silico</em> simulation as a third evidentiary
            substrate alongside randomized controlled trials and claims-based
            real-world evidence.
          </p>
          <p>
            If your organization works at any of those intersections, we&rsquo;d
            like to hear from you. <Link to="/contact">Reach the office of the CEO</Link>.
          </p>
        </article>

      </section>
    </main>
  );
}

export default AboutPage;
